/* eslint-disable @angular-eslint/prefer-inject */
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import {
    IQueryParams,
    PFWmsEntities,
    ProfileProvider,
    SafeAny,
} from '@pf/shared-common';
import {
    PutAwayListCreate,
    PutAwayListDto,
    PutAwayListRead,
    PutAwayListSearchParams,
} from '../entities/put-away-list.dto';
import { PutAwayListDataService } from '../infrastructure/put-away-lists/put-away-list.data.service';
import { PutAwayListStore } from '../infrastructure/put-away-lists/put-away-list.store';
import { PutAwayListMapper } from '../infrastructure/put-away-lists/put-away-list.mapper';
import { ResourceAuth } from '@control-tower/platform-wms';
import { filter, map, Observable, switchMap, tap } from 'rxjs';
import { WarehouseFacade } from './warehouse.facade';
import { WarehouseDto } from '../entities/warehouse.dto';
import { isValue } from '@pf/shared-utility';

@Injectable()
export class PutAwayListFacade extends AbstractManageEntityFacade<
    PutAwayListDto,
    PutAwayListRead,
    PutAwayListCreate,
    PutAwayListSearchParams
> {
    private readonly warehouseFacade = inject(WarehouseFacade);
    private readonly profileProvider = inject(ProfileProvider);
    nameField: keyof PutAwayListDto = 'name';

    constructor(
        dataService: PutAwayListDataService,
        store: PutAwayListStore,
        mapper: PutAwayListMapper
    ) {
        super(dataService, null, store, mapper);
        this.listenForRealTimeEvents(PFWmsEntities.PutAwayList, {
            customEntityMapping: (entity: PutAwayListRead) => {
                return dataService.get$(entity.id, true);
            },
        });
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField as SafeAny
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof PutAwayListDto, string>> {
        return { name: searchText };
    }

    override getDefaultSearchParams$(
        params: IQueryParams<PutAwayListDto>
    ): Observable<IQueryParams<PutAwayListDto>> {
        return this.warehouseFacade.addDefaultWarehouseParam(params);
    }

    override applyResourceAuth(body: PutAwayListCreate): void {
        const locationId = this.warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    loadPendingPutAwayLists$() {
        return this.searchAll$(
            {
                pageIndex: 1,
                pageSize: 50,
                filters: {
                    warehouseId: this.warehouseFacade.active?.id,
                    status: 'Pending',
                },
            },
            false
        ).pipe(map(() => true));
    }

    pendingPutAwayLists$() {
        return this.warehouseFacade.active$.pipe(
            filter(isValue),
            switchMap((warehouse: WarehouseDto) => {
                return this.store.filter$(
                    (list: PutAwayListRead) =>
                        list.warehouse?.id === warehouse.id &&
                        list.status === 'Pending'
                );
            }),
            map((lists: PutAwayListRead[]) => this.mapper.toDTOList(lists))
        );
    }

    loadActivePutAwayLists$() {
        return this.searchAll$(
            {
                pageIndex: 1,
                pageSize: 50,
                filters: {
                    status: 'Active',
                    assignedTo: this.profileProvider.profile.userId,
                },
            },
            false
        ).pipe(map(() => true));
    }

    activePutAwayLists$() {
        return this.warehouseFacade.active$.pipe(
            filter(isValue),
            switchMap((warehouse: WarehouseDto) => {
                return this.store.filter$(
                    (list: PutAwayListRead) =>
                        list.warehouse?.id === warehouse.id &&
                        list.status === 'Active' &&
                        list.assignedTo === this.profileProvider.profile.userId
                );
            }),
            map((lists: PutAwayListRead[]) => this.mapper.toDTOList(lists))
        );
    }

    completePutAwayLine$(putAwayLineId: string, containerId?: string) {
        return (
            this.dataService as PutAwayListDataService
        ).completePutAwayLine$(putAwayLineId, containerId);
    }
}
